import { styled } from '@linaria/react'
import { Link } from 'react-router-dom'
import { MinimalButton, MinimalDisplaySection, MinimalLandingSection } from '@ovasabi/ui-minimal'
import { from, minimalVars as v, until } from '@ovasabi/ui-minimal/tokens'

/**
 * The one left and right edge of the landing page.
 *
 * The header band, the main column and the footer each set this padding
 * separately, so a change to one left the others behind. Every band now
 * reads the same measure, and stays on the same edge at every width.
 */
const contentEdge = `
  padding-inline: ${v.space.md};
  ${from('page')} {
    padding-inline: ${v.space.lg};
  }
`

export const LandingStyle = {
  Page: styled.div`
    height: 100%;
    overflow-y: auto;
    overflow-x: clip;
    background: ${v.color.bgApp};
    color: ${v.color.textPrimary};
    scroll-behavior: smooth;
    @media (prefers-reduced-motion: reduce) {
      scroll-behavior: auto;
    }
  `,
  Nav: styled.header`
    position: sticky;
    top: 0;
    z-index: 30;
    background: rgba(250, 248, 245, 0.86);
    backdrop-filter: blur(18px);
    box-shadow: 0 10px 26px rgba(23, 39, 62, 0.06);
  `,
  NavInner: styled.div`
    max-width: 1280px;
    margin: auto;
    padding-block: ${v.space.xs};
    ${contentEdge}
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${v.space.sm};
    min-height: 72px;
    ${until('page')} {
      flex-wrap: wrap;
      column-gap: ${v.space.xs};
      row-gap: ${v.space.xs};
    }
  `,
  Brand: styled(Link)`
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    text-decoration: none;
    img {
      width: clamp(120px, 15vw, 160px);
      height: auto;
    }
  `,
  DeliverPill: styled(MinimalButton)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${v.color.bgSurface};
    border: 1px solid #E6E1DA;
    padding: 6px 14px;
    border-radius: ${v.radius.pill};
    font-size: ${v.typography.captionSize};
    font-weight: ${v.typography.weightBold};
    color: #333333;
    cursor: pointer;
    font-family: inherit;
    transition: border-color 140ms ease, background-color 140ms ease, color 140ms ease;
    min-width: 0;
    max-width: 100%;
    flex-wrap: wrap;
    ${until('page')} {
      padding-inline: ${v.space.xs};
      flex: 1 1 120px;
    }
    strong {
      overflow-wrap: anywhere;
      color: #E31837;
    }
    &:hover {
      border-color: #E31837;
      background: #FFF8F8;
      box-shadow: 0 2px 8px rgba(227, 24, 55, 0.08);
    }
    &:focus-visible {
      outline: 2px solid #E31837;
      outline-offset: 2px;
    }
  `,
  NavLinks: styled.nav`
    display: flex;
    align-items: center;
    gap: ${v.space.md};
    a {
      color: inherit;
      text-decoration: none;
      font-weight: 700;
      min-height: 40px;
      display: inline-flex;
      align-items: center;
      transition: color 0.15s ease;
    }
    a:hover {
      color: #E31837;
    }
    a:last-child {
      color: ${v.color.textInverse};
    }
    ${until('page')} {
      flex: 1 0 auto;
      flex-wrap: nowrap;
      justify-content: flex-end;
      align-items: center;
      gap: ${v.space.sm};
      > a:last-child {
        padding: ${v.space.xs} ${v.space.md};
        min-height: 40px;
      }
      > a:not(:last-child):not(:nth-last-child(2)) {
        display: none;
      }
    }
  `,
  Main: styled.main`
    max-width: 1280px;
    margin: auto;
    ${contentEdge}
  `,
  Hero: styled(MinimalDisplaySection)`
    && {
      margin-block: ${v.space.md} ${v.space.lg};
      border: 0;
      padding: 0;
      box-shadow: none;
      background: transparent;
      min-height: auto;
      overflow: visible;
      gap: ${v.space.lg};
    }
    && h1 {
      /* Tight leading stays hand-set. lineHeightTight resolves to 1.08, not 1.06. */
      /* displaySize replaces the old clamp within tolerance: floor +4%, ceiling -1%. */
      font-size: ${v.typography.displaySize};
      font-weight: 900;
      line-height: 1.06;
      letter-spacing: -0.04em;
      max-width: 22ch;
    }
    && p {
      max-width: 44ch;
      font-size: ${v.typography.bodySize};
      line-height: ${v.typography.lineHeightBody};
      color: #4A4A4A;
    }
    && > div:first-child {
      gap: ${v.space.sm};
    }
    && > div:nth-child(2) {
      position: relative;
      overflow: visible;
      min-height: clamp(320px, 34vw, 430px);
    }
    ${from('page')} {
      && {
        margin-block: ${v.space.lg};
        grid-template-columns: 1.15fr 1fr;
      }
    }
    ${until('page')} {
      && {
        margin-block: ${v.space.xs} ${v.space.md};
        gap: ${v.space.md};
      }
      && h1 {
        font-size: clamp(1.75rem, 6.5vw, 2.25rem);
        line-height: 1.15;
      }
      && p {
        font-size: ${v.typography.bodySize};
        line-height: 1.4;
      }
      && > div:nth-child(2) {
        min-height: auto;
        max-height: none;
        height: auto;
        overflow: hidden;
        border-radius: 24px;
      }
    }
  `,
  Accent: styled.span`
    color: #E31837;
  `,
  HeroPhotoWrap: styled.div`
    position: relative;
    width: 100%;
    height: 100%;
  `,
  HeroPhoto: styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  `,
  HeroBadge: styled.div`
    position: absolute;
    bottom: ${v.space.xs};
    left: ${v.space.xs};
    z-index: 2;
    background: rgba(26, 26, 26, 0.88);
    backdrop-filter: blur(8px);
    color: ${v.color.textInverse};
    padding: ${v.space.xs} ${v.space.sm};
    border-radius: ${v.radius.pill};
    display: inline-flex;
    align-items: center;
    gap: ${v.space.xs};
    font-size: ${v.typography.captionSize};
    font-weight: ${v.typography.weightBold};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    span.star {
      color: #FFC043;
    }
    ${until('page')} {
      position: absolute;
      bottom: 12px;
      left: 12px;
    }
  `,
  HeroMeals: styled.div`
    position: relative;
    width: 100%;
    min-height: clamp(380px, 42vw, 480px);
    isolation: isolate;
    ${until('page')} {
      display: block;
      min-height: auto;
      position: relative;
    }
  `,
  HeroMealLead: styled(Link)`
    position: absolute;
    top: 0;
    right: 0;
    width: min(88%, 500px);
    height: 82%;
    overflow: hidden;
    border-radius: 28px;
    color: ${v.color.textInverse};
    text-decoration: none;
    background: #1A1A1A;
    box-shadow: 0 24px 54px rgba(15, 23, 42, 0.18);
    transform: rotate(1deg);
    transition: transform 180ms ease, box-shadow 180ms ease;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0.02) 40%, rgba(0, 0, 0, 0.78) 100%);
    }
    > span {
      position: absolute;
      left: 20px;
      right: 20px;
      bottom: 22px;
      z-index: 1;
      display: grid;
      gap: 4px;
      max-width: 65%;
    }
    strong {
      /* The overlay dish name stays fluid: h2Size is fixed at 22px and lifts the floor by 31%. */
      font-size: clamp(1.05rem, 1.5vw, 1.35rem);
      line-height: 1.2;
      font-weight: 900;
    }
    small {
      color: rgba(255, 255, 255, 0.86);
      font-weight: ${v.typography.weightBold};
    }
    &:hover {
      transform: translateY(-3px) rotate(0deg);
      box-shadow: 0 28px 64px rgba(15, 23, 42, 0.22);
    }
    ${until('page')} {
      position: relative;
      width: 100%;
      height: 280px;
      min-height: 280px;
      transform: none;
      border-radius: 24px;
      overflow: hidden;
      display: block;
      > span {
        max-width: 100%;
      }
    }
  `,
  HeroMealList: styled.div`
    display: contents;
  `,
  HeroMealMini: styled(Link)`
    position: absolute;
    z-index: 2;
    display: grid;
    grid-template-columns: 70px minmax(0, 1fr);
    align-items: center;
    gap: 12px;
    min-width: 0;
    width: min(52%, 248px);
    padding: 10px 14px;
    border: 1px solid rgba(226, 219, 210, 0.88);
    border-radius: 20px;
    color: inherit;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.96);
    backdrop-filter: blur(12px);
    box-shadow: 0 16px 38px rgba(15, 23, 42, 0.14);
    transition: transform 180ms ease, box-shadow 180ms ease;
    &:nth-child(1) {
      left: 0;
      top: 36px;
      transform: rotate(-2deg);
    }
    &:nth-child(2) {
      right: ${v.space.md};
      bottom: ${v.space.md};
      transform: rotate(2deg);
    }
    img {
      width: 70px;
      height: 70px;
      object-fit: cover;
      border-radius: 14px;
      display: block;
    }
    span {
      min-width: 0;
      display: grid;
      gap: 2px;
    }
    strong,
    small {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    strong {
      /* The dish name stays at 0.9rem: captionSize at 13px leaves the name and its meta line near-equal. */
      font-size: 0.88rem;
      line-height: 1.25;
      font-weight: 850;
      color: ${v.color.textPrimary};
    }
    small {
      color: ${v.color.brand};
      font-size: ${v.typography.metaSize};
      font-weight: ${v.typography.weightBold};
    }
    &:hover {
      transform: translateY(-3px) rotate(0deg);
      box-shadow: 0 20px 44px rgba(15, 23, 42, 0.18);
    }
    ${until('page')} {
      display: none;
    }
  `,
  Cta: styled(Link)`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${v.space.sm};
    padding: ${v.space.sm} ${v.space.lg};
    min-height: 46px;
    white-space: nowrap;
    border-radius: ${v.radius.pill};
    background: #E31837;
    color: ${v.color.textInverse};
    font-weight: 800;
    text-decoration: none;
    border: 1px solid transparent;
    transition: transform 0.15s ease, opacity 0.15s ease;
    &[data-variant='ghost'] {
      color: ${v.color.textPrimary};
      background: ${v.color.bgSurface};
      border-color: #E2DBD2;
    }
    &:hover {
      transform: translateY(-1px);
      opacity: 0.94;
    }
    &:focus-visible {
      outline: 3px solid #E31837;
      outline-offset: 3px;
    }
  `,
  Trust: styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${v.space.sm};
    padding-block: ${v.space.xs};
    font-size: ${v.typography.captionSize};
    color: ${v.color.textSecondary};
    a {
      color: inherit;
      font-weight: ${v.typography.weightBold};
      text-decoration: underline;
      text-underline-offset: 4px;
    }
  `,
  Section: styled(MinimalLandingSection)`
    && {
      padding-block: ${v.space.xl};
      gap: ${v.space.lg};
      border-top: 0;
    }
    /* The primitive centres this copy block by default, which pushed the
       heading 260px right of the content edge. Text alignment alone did not
       undo it: the box itself was centred. Start it on the shared edge. */
    && > div:first-child {
      justify-self: start;
      align-items: flex-start;
      text-align: left;
      width: 100%;
    }
    /* Ensure action rows in section headers align to the left matching the copy */
    && > div:first-child > div:last-child {
      justify-content: flex-start;
      width: 100%;
    }
    && h2 {
      /* Section and banner titles share one h1 token, so both read 28 to 40px. */
      font-size: ${v.typography.h1Size};
      font-weight: 900;
      letter-spacing: -0.03em;
    }
    && p {
      text-align: left;
    }
  `,
  PlanBand: styled.div`
    display: grid;
    gap: ${v.space.md};
    align-items: center;
    padding: 24px;
    border: 1px solid rgba(226, 219, 210, 0.7);
    border-radius: 28px;
    background: linear-gradient(135deg, #fff9f0 0%, #fff4e6 100%);
    box-shadow: 0 12px 34px rgba(23, 39, 62, 0.06);

    ${from('page')} {
      grid-template-columns: minmax(0, 1.25fr) minmax(240px, 0.85fr);
      padding: 36px 40px;
      gap: ${v.space.xl};
    }
    ${until('hand')} {
      padding: 18px 16px;
      border-radius: 20px;
    }
  `,
  PlanBandCopy: styled.div`
    display: grid;
    gap: 12px;
    h3 {
      margin: 0;
      /* The plan band title stays fluid: h2Size is fixed at 22px and shrinks the heading on desktop. */
      font-size: clamp(1.4rem, 2.4vw, 2rem);
      line-height: 1.15;
      font-weight: 900;
      letter-spacing: -0.03em;
      color: #1a1a1a;
    }
    p {
      margin: 0;
      color: ${v.color.textSecondary};
      line-height: ${v.typography.lineHeightBody};
      max-width: 58ch;
    }
  `,
  PlanDualGrid: styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    margin-block: 4px;
    ${from('hand')} {
      grid-template-columns: 1fr 1fr;
    }
  `,
  PlanDualCard: styled.div`
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(230, 222, 212, 0.9);
    border-radius: 16px;
    padding: 12px 14px;
    display: grid;
    gap: 4px;
    strong {
      font-size: 0.86rem;
      font-weight: 800;
      color: #1a1a1a;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    span {
      font-size: 0.8rem;
      color: #555;
      line-height: 1.35;
    }
  `,
  PlanBandActions: styled.div`
    display: flex;
    gap: ${v.space.xs};
    flex-wrap: wrap;
    margin-top: 4px;
    a {
      flex: 0 1 auto;
    }
    ${until('hand')} {
      flex-direction: column;
      width: 100%;
      a {
        width: 100%;
        text-align: center;
        justify-content: center;
      }
    }
  `,
  PlanBandMedia: styled.div`
    min-height: 220px;
    border-radius: 20px;
    overflow: hidden;
    background: #f5efeb;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    img {
      display: block;
      width: 100%;
      height: 100%;
      min-height: 220px;
      max-height: 320px;
      object-fit: cover;
    }
  `,
  CategoryRail: styled.div`
    display: flex;
    align-items: center;
    gap: ${v.space.xs};
    overflow-x: auto;
    padding-bottom: ${v.space.xs};
    margin-bottom: ${v.space.md};
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `,
  CategoryPill: styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: ${v.radius.pill};
    border: 1px solid ${v.color.borderSubtle};
    background: ${v.color.bgSurface};
    color: ${v.color.textPrimary};
    font-size: ${v.typography.captionSize};
    font-weight: ${v.typography.weightBold};
    white-space: nowrap;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;
    &[data-active='true'] {
      background: #1A1A1A;
      color: ${v.color.textInverse};
      border-color: #1A1A1A;
    }
    &:hover:not([data-active='true']) {
      background: ${v.color.bgSurfaceHover};
      border-color: ${v.color.borderStrong};
    }
  `,
  Grid: styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: ${v.space.md};
    align-items: stretch;
    ${from('page')} {
      gap: ${v.space.lg};
    }
  `,
  Tile: styled(Link)`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
    color: inherit;
    text-decoration: none;
    background: ${v.color.bgSurface};
    border: 1px solid ${v.color.borderSubtle};
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
    transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.2s ease;
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
      border-color: #E31837;
    }
    &:focus-visible {
      outline: 3px solid #E31837;
      outline-offset: 3px;
    }
  `,
  Media: styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    background: #F5EFEB;
    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
      transition: transform 0.3s ease;
    }
    &:hover img {
      transform: scale(1.04);
    }
  `,
  CardBadge: styled.span`
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 2;
    background: rgba(26, 26, 26, 0.88);
    backdrop-filter: blur(4px);
    color: ${v.color.textInverse};
    /* An 11px overlay badge keeps the tile photo dominant: metaSize at 12px widens the chip. */
    font-size: 0.6875rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 3px 8px;
    border-radius: ${v.radius.pill};
    &[data-variant='bestseller'] {
      background: #00875A;
    }
    &[data-variant='popular'] {
      background: #E31837;
    }
  `,
  DiscountBadge: styled.span`
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
    background: #E31837;
    color: ${v.color.textInverse};
    /* An 11px overlay badge keeps the tile photo dominant: metaSize at 12px widens the chip. */
    font-size: 0.6875rem;
    font-weight: 800;
    padding: 3px 7px;
    border-radius: ${v.radius.pill};
  `,
  TileBody: styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: ${v.space.xs};
    padding: ${v.space.sm};
    h3 {
      margin: 0;
      /* Tile titles stay at 1.05rem: h2Size resolves to 22px and would clip dish names in a 260px tile. */
      font-size: 1.05rem;
      font-weight: 800;
      line-height: 1.3;
      white-space: normal;
      overflow-wrap: anywhere;
      color: #1A1A1A;
    }
    p.chef {
      margin: 0;
      color: ${v.color.textSecondary};
      font-size: ${v.typography.captionSize};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `,
  TileFoot: styled.div`
    margin-top: auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${v.space.xs};
    padding-top: ${v.space.xs};
  `,
  PriceRow: styled.div`
    display: flex;
    flex-direction: column;
    span.price {
      /* A tile price stays at 1.125rem: h2Size is the 22px section-header size, not a price. */
      font-size: 1.125rem;
      font-weight: 900;
      color: #E31837;
    }
    span.rating {
      font-size: ${v.typography.captionSize};
      color: #666666;
      font-weight: 600;
    }
  `,
  AddBtn: styled.span`
    width: 34px;
    height: 34px;
    border-radius: ${v.radius.pill};
    background: #E31837;
    color: ${v.color.textInverse};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    /* The plus glyph stays at 1.25rem: h2Size is 22px and oversizes a glyph inside the fixed 34px circle. */
    font-size: 1.25rem;
    font-weight: 800;
    line-height: 1;
    flex-shrink: 0;
    box-shadow: 0 2px 6px rgba(227, 24, 55, 0.28);
    transition: transform 0.15s ease, background 0.15s ease;
    &:hover {
      background: #C5291C;
      transform: scale(1.08);
    }
  `,
  Steps: styled.ol`
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: ${v.space.lg};
    ${from('page')} {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    li {
      padding-top: ${v.space.md};
    }
    span {
      color: #E31837;
      font-weight: 900;
    }
    h3 {
      /* A step title stays at 1.25rem: h2Size is the 22px section-header size, not a step label. */
      font-size: 1.25rem;
      margin-block: ${v.space.sm};
    }
    p {
      color: ${v.color.textSecondary};
      margin: 0;
      line-height: ${v.typography.lineHeightBody};
    }
  `,
  ChefBanner: styled.section`
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #17273E 0%, #0F172A 100%);
    color: ${v.color.textInverse};
    padding: 36px 28px;
    border-radius: 28px;
    margin-block: ${v.space.xl};
    display: flex;
    flex-direction: column-reverse;
    align-items: center;
    justify-content: space-between;
    gap: ${v.space.xl};
    box-shadow: 0 16px 36px -6px rgba(15, 23, 42, 0.25);

    ${from('page')} {
      flex-direction: row;
      padding: 48px 56px;
    }
  `,
  ChefBannerContent: styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${v.space.md};
    max-width: 540px;
    z-index: 1;
  `,
  ChefBannerEyebrow: styled.span`
    font-size: ${v.typography.captionSize};
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #FF5A79;
  `,
  ChefBannerTitle: styled.h2`
    margin: 0;
    color: ${v.color.textInverse};
    font-size: ${v.typography.h1Size};
    font-weight: 800;
    line-height: 1.15;
  `,
  ChefBannerText: styled.p`
    margin: 0;
    color: rgba(255, 255, 255, 0.84);
    font-size: ${v.typography.bodySize};
    line-height: ${v.typography.lineHeightBody};
  `,
  ChefBannerMedia: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    flex-shrink: 0;
    padding: 12px;

    &::before {
      content: '';
      position: absolute;
      width: 220px;
      height: 220px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(230, 20, 69, 0.25) 0%, transparent 70%);
      pointer-events: none;
    }

    img {
      position: relative;
      z-index: 1;
      filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.35));
    }
  `,
  Vendor: styled(MinimalLandingSection)`
    && {
      background: #1A1A1A;
      color: ${v.color.textInverse};
      padding: ${v.space.lg} ${v.space.xl};
      border-radius: 24px;
      margin-block: ${v.space.lg};
    }
    && h2 {
      color: ${v.color.textInverse};
      font-size: ${v.typography.h1Size};
    }
    && p {
      color: rgba(255, 255, 255, 0.78);
      max-width: 48ch;
      font-size: ${v.typography.bodySize};
    }
  `,
  KitchenGrid: styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
    gap: ${v.space.md};
  `,
  KitchenCard: styled(Link)`
    display: flex;
    align-items: center;
    gap: ${v.space.md};
    padding: ${v.space.md};
    background: ${v.color.bgSurface};
    border: 1px solid ${v.color.borderSubtle};
    border-radius: 20px;
    text-decoration: none;
    color: inherit;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
    transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.2s ease;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.07);
      border-color: #E31837;
    }
  `,
  KitchenAvatar: styled.div`
    width: 64px;
    height: 64px;
    border-radius: 16px;
    overflow: hidden;
    flex-shrink: 0;
    background: ${v.color.bgSurfaceAlt};
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `,
  KitchenMeta: styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 3px;
    h3 {
      margin: 0;
      /* Kitchen names stay at 1.05rem: h2Size resolves to 22px in a 144px text column. */
      font-size: 1.05rem;
      font-weight: 800;
      white-space: normal;
      overflow-wrap: anywhere;
      color: #1A1A1A;
    }
    p.cuisine {
      margin: 0;
      color: ${v.color.textSecondary};
      font-size: ${v.typography.captionSize};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    span.open {
      font-size: ${v.typography.metaSize};
      font-weight: 700;
      color: #00875A;
    }
  `,
  Footer: styled.footer`
    border-top: 1px solid ${v.color.borderSubtle};
    background: ${v.color.bgSurface};
    padding-top: 48px;
    padding-bottom: 36px;
    margin-top: ${v.space.xl};
  `,
  FooterInner: styled.div`
    max-width: 1280px;
    margin: auto;
    ${contentEdge}
    display: flex;
    flex-direction: column;
    gap: 36px;
  `,
  FooterTop: styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;

    ${from('hand')} {
      grid-template-columns: 1.5fr repeat(3, minmax(0, 1fr));
      gap: 32px;
    }
  `,
  FooterBrandCol: styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 320px;
    img {
      width: 136px;
      height: auto;
    }
    p {
      margin: 0;
      font-size: ${v.typography.bodySize};
      color: ${v.color.textSecondary};
      line-height: ${v.typography.lineHeightBody};
    }
  `,
  FooterNavCol: styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    h4 {
      margin: 0;
      font-size: ${v.typography.captionSize};
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: ${v.color.textPrimary};
    }
    nav {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    a {
      color: ${v.color.textSecondary};
      text-decoration: none;
      font-size: ${v.typography.bodySize};
      transition: color 0.15s ease;
      &:hover {
        color: #E31837;
      }
    }
  `,
  FooterBottom: styled.div`
    border-top: 1px solid ${v.color.borderSubtle};
    padding-top: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    color: ${v.color.textTertiary};
    font-size: ${v.typography.captionSize};

    ${from('hand')} {
      flex-direction: row;
    }
  `,
  FooterLegal: styled.div`
    display: flex;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
    a {
      color: ${v.color.textTertiary};
      text-decoration: none;
      font-size: ${v.typography.captionSize};
      &:hover {
        color: ${v.color.textPrimary};
      }
    }
  `,
  FooterLinks: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${v.space.md} ${v.space.lg};
    a {
      color: ${v.color.textSecondary};
      text-decoration: none;
      font-size: ${v.typography.bodySize};
      &:hover {
        color: #E31837;
      }
    }
  `,
  FooterCopy: styled.p`
    margin: 0;
    font-size: ${v.typography.captionSize};
    color: ${v.color.textTertiary};
  `,
}
